import React, {
    ReactElement,
    useRef,
    useState,
    FC,
} from 'react';

import {useCustomLinkHandler} from 'features/provide-custom-link/hooks/use-custom-link-handler';
import {ICustomLinkCommonProps, ICustomLinkOpenModalRef} from 'features/provide-custom-link/model/types';
import {IExternalLinkProperties, TLinkDto, ELinkType} from 'shared/types/links';

import {CustomLinkModal} from '../modal';
import {CustomLinkSubmenu} from '../submenu';
import {LinkView} from '../view';
import s from './custom-link.module.less';

export interface ICustomLinkProps extends ICustomLinkCommonProps {
    component?: ReactElement;
    externalProperties?: IExternalLinkProperties;
    isSubmenuItem?: boolean; // флаг, означающий что customLink находится внутри кебаба (обнуляет стили)
}

export const CustomLink: FC<ICustomLinkProps> = ({
    link,
    component,
    extraStateData,
    externalProperties,
    isSubmenuItem = false,
    actionCode,
}: ICustomLinkProps) => {
    const [isLoading, setLoading] = useState(false);

    const {
        url = '/',
        urlType,
        linkProperties,
    } = link ?? {};

    const contentModalRef = useRef<ICustomLinkOpenModalRef>(null);

    const getLinkAliasTitle = (): string | undefined | null => {
        if (externalProperties?.title) {
            return externalProperties.title;
        }

        if (linkProperties?.title && linkProperties.title !== '') {
            return linkProperties.title;
        }

        if (linkProperties?.type === 'link' && url !== null) {
            return url;
        }

        return undefined;
    };

    const {handleLinkClick, checkIsLoading, urlWithParams} = useCustomLinkHandler({
        link: link!,
        extraStateData,
        actionCode,
        setLoading,
    });

    const properties: TLinkDto['linkProperties'] = {
        ...linkProperties,
        title: getLinkAliasTitle(),
        icon: externalProperties?.icon ?? linkProperties?.icon,
    };

    if (!link) return null;

    return (
        <>
            <div
                className={s['custom-link']}
                onClick={() => handleLinkClick(contentModalRef.current?.showModalWithLink)}
            >
                {urlType === ELinkType.SUBMENU ? (
                    <CustomLinkSubmenu
                        modalRef={contentModalRef}
                        childrenCustomLinks={link?.children}
                        isLoading={isLoading}
                        setLoading={setLoading}
                        actionCode={actionCode}
                    />
                ) : (
                    <a
                        href={urlWithParams}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => {
                            if (urlType !== ELinkType.EXTERNAL) e.preventDefault();
                        }}
                    >
                        {component ?? (
                            <LinkView
                                properties={properties}
                                isLoading={checkIsLoading() || isLoading}
                                isSubmenuItem={isSubmenuItem}
                            />
                        )}
                    </a>
                )}
            </div>
            <CustomLinkModal
                link={link}
                ref={contentModalRef}
            />
        </>
    );
};
